/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';

import { TextField } from '@coursera/cds-core';

import type { CmlContent } from 'bundles/cml/legacy/types/Content';
import { getDtdId } from 'bundles/cml/legacy/utils/CMLUtils';
import { toCML, toPlainText } from 'bundles/cml/legacy/utils/plainTextConverter';
import placeCaretAtEnd from 'bundles/phoenix/lib/placeCaretAtEnd';

import _t from 'i18n!nls/cml';

export type Props = {
  cml?: CmlContent | null;
  id?: string;
  ariaLabel?: string;
  ariaDescribedBy?: string;
  isFocused?: boolean;
  placeholder?: string;
  onChange: (cml: CmlContent, html?: string) => void;
};

type State = {
  plainText: string | boolean;
};

class FallbackEditor extends React.Component<Props, State> {
  declare ref: React.MutableRefObject<HTMLTextAreaElement | null>;

  static defaultProps = {
    isFocused: false,
    placeholder: '',
    onChange: () => {},
  };

  constructor(props: Props) {
    super(props);
    const plainText = toPlainText(props.cml);
    this.state = { plainText };
    this.ref = React.createRef();
  }

  componentDidMount() {
    if (this.props.isFocused) {
      this.focus();
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    this.setState({
      plainText: toPlainText(nextProps.cml),
    });
  }

  componentDidUpdate(prevProps: Props) {
    if (!prevProps.isFocused && this.props.isFocused) {
      this.focus();
    }
  }

  emitChange = () => {
    const { cml, onChange } = this.props;
    const text = this.ref.current?.value ?? '';
    onChange(toCML(text, getDtdId(cml)));
  };

  focus() {
    placeCaretAtEnd(this.ref.current);
  }

  render() {
    const { id, ariaLabel, ariaDescribedBy, placeholder } = this.props;
    const { plainText } = this.state;

    const error = plainText === false;
    const value = typeof plainText === 'string' ? plainText : '';

    return (
      <div data-testid="fallback-editor" className="rc-FallbackEditor">
        <TextField
          aria-label={ariaLabel ?? _t('text editor')}
          id={id}
          value={value}
          readOnly={error}
          multiline
          minRows={3}
          maxRows={6}
          onChange={this.emitChange}
          inputRef={this.ref}
          aria-describedby={ariaDescribedBy}
          placeholder={placeholder}
          validationStatus={error ? 'error' : undefined}
          validationLabel={_t(
            'This browser does not support editing formatted content. Please use a supported browser.'
          )}
          fullWidth
        />
      </div>
    );
  }
}

export default FallbackEditor;
